import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, X, CheckCircle, AlertCircle, Users, Download, AlertTriangle, HelpCircle, Info } from 'lucide-react';
import { userManagementService, HistoricalEventsImportResponse } from '../../services/userManagementService';
import { useNotification } from '../../hooks/useNotification';
import { useAppSelector } from '../../store/hooks';
import Button from '../UI/Button';
import Modal from '../UI/Modal';
import HistoricalEventImportErrorReview from './HistoricalEventImportErrorReview';
import HistoricalEventImportSkippedDuplicates from './HistoricalEventImportSkippedDuplicates';
import * as XLSX from 'xlsx';

interface HistoricalEventImportProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface UploadResult {
  success: boolean;
  data?: HistoricalEventsImportResponse['data'];
  error?: string;
}

const HistoricalEventImport: React.FC<HistoricalEventImportProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { showNotification } = useNotification();
  const { theme } = useAppSelector((state) => state.ui);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showErrorReview, setShowErrorReview] = useState(false);
  const [showSkippedDuplicates, setShowSkippedDuplicates] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const handleFileSelect = (file: File) => {
    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];
    
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      showNotification('Please select a valid Excel file (.xlsx or .xls)', 'error');
      return;
    }
    
    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      showNotification('File size must be less than 10MB', 'error');
      return;
    }
    
    setSelectedFile(file);
    setUploadResult(null);
    setShowErrorReview(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    try {
      const response = await userManagementService.importHistoricalEvents(selectedFile, true);
      
      if (response.success) {
        setUploadResult({ success: true, data: response.data });
        showNotification(response.message || 'Import completed successfully', 'success');
        
        // If there are skipped duplicates, show skipped duplicates modal
        if (response.data && response.data.skipped_duplicates && response.data.skipped_duplicates.length > 0) {
          setShowSkippedDuplicates(true);
        }
        
        // If there are errors, show error review modal
        if (response.data && response.data.errors && response.data.errors.length > 0) {
          setShowErrorReview(true);
        } else if (!response.data?.skipped_duplicates || response.data.skipped_duplicates.length === 0) {
          onSuccess(); // Refresh the user list only if no modals to show
        }
      } else {
        setUploadResult({ 
          success: false, 
          error: response.error?.message || 'Import failed' 
        });
        showNotification(response.error?.message || 'Import failed', 'error');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Import failed';
      setUploadResult({ success: false, error: errorMessage });
      showNotification(errorMessage, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setShowErrorReview(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleDownloadTemplate = () => {
    try {
      // Create workbook
      const workbook = XLSX.utils.book_new();
      
      // Define headers
      const headers = [
        'Full Name/Nickname',
        'Name',
        'Count',
        'Phone Number',
        'Number (2)',
        'Companion Name (1)',
        'Phone Number',
        'Companion Name (2)',
        'Phone Number',
        'Invited By',
        'Language',
        'Comments'
      ];
      
      // Sample data for 2 rows
      const sampleData = [
        [
          'Ahmed Ali (Full Name)',
          'Ahmed Ali',
          '1',
          '962771234567',
          '',
          '',
          '',
          '',
          '',
          'John Smith',
          'Arabic',
          'VIP Guest'
        ],
        [
          'Mr and Ms Ahmad (Dina)',
          'Ahmad',
          '2',
          '962779876543',
          '962775551234',
          'Dina',
          '962779998877',
          '',
          '',
          'Sarah Johnson',
          'English',
          'Regular guest with companion'
        ]
      ];
      
      // Create worksheet data
      const worksheetData = [headers, ...sampleData];
      
      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 20 }, // Full Name/Nickname
        { wch: 15 }, // Name
        { wch: 8 },  // Count
        { wch: 15 }, // Phone Number
        { wch: 12 }, // Number (2)
        { wch: 18 }, // Companion Name (1)
        { wch: 15 }, // Phone Number
        { wch: 18 }, // Companion Name (2)
        { wch: 15 }, // Phone Number
        { wch: 15 }, // Invited By
        { wch: 12 }, // Language
        { wch: 25 }  // Comments
      ];
      
      // Add worksheet to workbook with sheet name "Event Name"
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Event Name');
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'historical_events_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showNotification('Template downloaded successfully', 'success');
    } catch (error) {
      console.error('Error generating template:', error);
      showNotification('Failed to generate template', 'error');
    }
  };

  const handleErrorReviewClose = () => {
    setShowErrorReview(false);
    onSuccess(); // Refresh the user list after error review
    handleClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Import Historical Events"
        size="lg"
      >
        <div className="space-y-6">
          {/* Header with Actions */}
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              leftIcon={<Download className="h-4 w-4" />}
            >
              Download Template
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowHelpModal(true)}
              leftIcon={<HelpCircle className="h-4 w-4" />}
              title="View import instructions"
            >
              Help
            </Button>
          </div>

          {/* File Upload Area */}
          {!uploadResult && (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? theme === 'dark'
                    ? 'border-blue-500 bg-blue-900/20'
                    : 'border-blue-400 bg-blue-50'
                  : theme === 'dark'
                  ? 'border-gray-600 hover:border-gray-500'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
              
              {selectedFile ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center">
                    <FileSpreadsheet className="h-12 w-12 text-green-500" />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{selectedFile.name}</p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <div className="flex justify-center space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Change File
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleReset}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-center">
                    <Upload className="h-12 w-12 text-gray-400" />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Drop your Excel file here, or click to browse
                    </p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Supports .xlsx and .xls files up to 10MB
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Select File
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Upload Results */}
          {uploadResult && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg border ${
                uploadResult.success 
                  ? theme === 'dark'
                    ? 'bg-green-900/20 border-green-700'
                    : 'bg-green-50 border-green-200'
                  : theme === 'dark'
                  ? 'bg-red-900/20 border-red-700'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center">
                  {uploadResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                  )}
                  <h3 className={`text-sm font-medium ${
                    uploadResult.success 
                      ? theme === 'dark' ? 'text-green-300' : 'text-green-900'
                      : theme === 'dark' ? 'text-red-300' : 'text-red-900'
                  }`}>
                    {uploadResult.success ? 'Import Completed' : 'Import Failed'}
                  </h3>
                </div>
                <p className={`text-sm mt-1 ${
                  uploadResult.success 
                    ? theme === 'dark' ? 'text-green-200' : 'text-green-800'
                    : theme === 'dark' ? 'text-red-200' : 'text-red-800'
                }`}>
                  {uploadResult.success 
                    ? `Processed ${uploadResult.data?.total_processed || 0} rows`
                    : uploadResult.error
                  }
                </p>
              </div>

              {uploadResult.success && uploadResult.data && (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <div className="flex items-center">
                        <Users className={`h-4 w-4 mr-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Successful</span>
                      </div>
                      <p className={`text-2xl font-bold mt-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {uploadResult.data.successful}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <div className="flex items-center">
                        <AlertCircle className={`h-4 w-4 mr-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Failed</span>
                      </div>
                      <p className={`text-2xl font-bold mt-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {uploadResult.data.failed}
                      </p>
                    </div>
                  </div>

                  {/* Additional Stats */}
                  {uploadResult.data.duplicates_skipped > 0 && (
                    <div className={`border rounded-lg p-3 ${
                      theme === 'dark' 
                        ? 'bg-yellow-900/20 border-yellow-700' 
                        : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <AlertTriangle className={`h-4 w-4 mr-2 ${
                            theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                          }`} />
                          <span className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-yellow-300' : 'text-yellow-900'
                          }`}>
                            Duplicates Skipped: {uploadResult.data.duplicates_skipped}
                          </span>
                        </div>
                        {uploadResult.data.skipped_duplicates && uploadResult.data.skipped_duplicates.length > 0 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowSkippedDuplicates(true)}
                            className={theme === 'dark' 
                              ? 'text-yellow-300 border-yellow-700 hover:bg-yellow-900/30' 
                              : 'text-yellow-700 border-yellow-300 hover:bg-yellow-100'
                            }
                          >
                            View Details
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Errors Section */}
                  {uploadResult.data.errors && uploadResult.data.errors.length > 0 && (
                    <div className={`border rounded-lg p-4 ${
                      theme === 'dark' 
                        ? 'bg-red-900/20 border-red-700' 
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <h4 className={`text-sm font-medium mb-3 ${
                        theme === 'dark' ? 'text-red-300' : 'text-red-900'
                      }`}>
                        Errors Found ({uploadResult.data.errors.length})
                      </h4>
                      <p className={`text-sm mb-3 ${
                        theme === 'dark' ? 'text-red-200' : 'text-red-800'
                      }`}>
                        Some rows could not be imported. Click "Review Errors" to see details and fix them.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowErrorReview(true)}
                        className={theme === 'dark' 
                          ? 'text-red-300 border-red-700 hover:bg-red-900/30' 
                          : 'text-red-700 border-red-300 hover:bg-red-100'
                        }
                      >
                        Review Errors
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            {!uploadResult ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading}
                  loading={isUploading}
                >
                  {isUploading ? 'Importing...' : 'Import Historical Events'}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                onClick={handleClose}
              >
                Close
              </Button>
            )}
          </div>
        </div>
      </Modal>

      {/* Help/Instructions Modal */}
      <Modal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="Import Instructions"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <Info className="h-5 w-5 text-blue-600 mr-2" />
              <h3 className="text-sm font-medium text-blue-900">How to Import Historical Events</h3>
            </div>
            <ul className="text-sm text-blue-800 space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Upload an Excel file (.xlsx or .xls) with multiple sheets. Each sheet name represents an event name.</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span><strong>Required columns:</strong> Name, Phone Number</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span><strong>Optional columns:</strong> Full Name/Nickname, Count, Number (2), Companion Name (1), Phone Number (for companion), Companion Name (2), Phone Number (for companion 2), Invited By, Language, Comments</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Duplicate guests (by phone number) will be skipped automatically.</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Maximum file size: 10MB</span>
              </li>
            </ul>
          </div>

          <div className={`border rounded-lg p-4 ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gray-50 border-gray-200'
          }`}>
            <h4 className={`text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>Column Details:</h4>
            <div className={`text-sm space-y-1 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              <p><strong>Full Name/Nickname:</strong> Will be stored as a nickname for the guest</p>
              <p><strong>Name:</strong> Main guest name (will be split into first name and last name)</p>
              <p><strong>Count:</strong> Total number of people (guest + companions)</p>
              <p><strong>Phone Number:</strong> Main guest phone (first occurrence), Companion 1 phone (second occurrence), Companion 2 phone (third occurrence)</p>
              <p><strong>Companion Name (1/2):</strong> Names of companions. Companion is valid if name length &gt; 2 characters OR has phone number.</p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => setShowHelpModal(false)}
            >
              Got it
            </Button>
          </div>
        </div>
      </Modal>

      {/* Skipped Duplicates Modal */}
      {showSkippedDuplicates && uploadResult?.data?.skipped_duplicates && (
        <HistoricalEventImportSkippedDuplicates
          isOpen={showSkippedDuplicates}
          onClose={() => {
            setShowSkippedDuplicates(false);
            // If no error review, refresh and close
            if (!showErrorReview && (!uploadResult?.data?.errors || uploadResult.data.errors.length === 0)) {
              onSuccess();
              handleClose();
            }
          }}
          skippedDuplicates={uploadResult.data.skipped_duplicates}
        />
      )}

      {/* Error Review Modal */}
      {showErrorReview && uploadResult?.data?.errors && (
        <HistoricalEventImportErrorReview
          isOpen={showErrorReview}
          onClose={handleErrorReviewClose}
          errors={uploadResult.data.errors}
          importBatchId={uploadResult.data.import_batch_id}
        />
      )}
    </>
  );
};

export default HistoricalEventImport;

